import { type FC } from 'react';
import { DiffMethod } from 'react-diff-viewer';
import { useSelector } from 'react-redux';

import { type RootState } from '@editor2/reducers/root';
import { type ReorderConflict } from '@editor2/types/conflict';
import ConflictDiffView from '../common/ConflictDiffView';
import { getChildName, getParentByEntityString } from '../helpers';
import { getIncomingParent } from '@editor2/services/conflictResolutionService';

interface Props {
	conflict: ReorderConflict;
	courseId: number;
}

const InitialIncomingReorderView: FC<Props> = (props) => {
	const { conflict, courseId } = props;

	const courseState = useSelector((state: RootState) => state.courses[courseId]);

	const getInitNamesList = () => {
		if (!conflict.initial?.length) return; // if no elements in page

		const namesList = conflict.initial.map((id) => {
			const name = getChildName({ courseState, attribute: conflict.attribute, id });
			const isDeletedOrMoved = !conflict.incoming.includes(id);

			if (isDeletedOrMoved) {
				const parent = getIncomingParent({ state: courseState, attribute: conflict.attribute, id });
				return parent ? `${name} [ moved to ${parent.name} ]` : `${name} [ deleted ]`;
			} else {
				return name;
			}
		});

		return namesList.join('\n');
	};

	const getIncomingNamesList = () => {
		if (!conflict.initial?.length) return; // if no elements in page

		const namesList = conflict.incoming.map((id) => {
			const name = getChildName({ courseState, attribute: conflict.attribute, id });
			const isNewOrMoved = !conflict.initial.includes(id);

			if (isNewOrMoved) {
				const tempInitParent = getParentByEntityString({
					state: courseState.tempInitCourse,
					entityString: conflict.attribute,
					childId: id
				});

				if (conflict.attribute === 'elements') {
					return tempInitParent
						? `${name} [ moved from ${tempInitParent.name} ]`
						: `${name} [ new or moved from ]`; // in case page elements weren't loaded yet
				}

				return tempInitParent ? `${name} [ moved from ${tempInitParent.name} ]` : `${name} [ new ]`;
			}

			return name;
		});

		return namesList.join('\n');
	};

	return (
		<ConflictDiffView
			oldValue={getInitNamesList()}
			newValue={getIncomingNamesList()}
			compareMethod={DiffMethod.LINES}
		/>
	);
};

export default InitialIncomingReorderView;
